import {FormControl} from "@angular/forms";
import * as IBAN from 'iban';

/**
 * Class for validatiing user inputs. has some static validation methods
 */
export class GlobalValidators {
    static readonly STREET_MISSING_NUMBER_INDEX = "missingHouseNumber";


    /**
     * Validating an email address
     * @param control
     * @returns {Object|null}
     */
    static validateEmail(control: FormControl) {
        /**
         * Regular expression that also exceps unicodes in emails
         * https://gist.github.com/BRIMIL01/969095
         * we validate after RFC 2822 because Symfony (Swiftmailer) will give problems otherwise
         * @type {RegExp}
         */
        let emailExpression = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

        // test the value
        if (emailExpression.test(control.value) === false) {
            return { emailSucks: true };
        }
        return null;
    }

    /**
     * Validates the address/street if a housenumber is included
     * @param control
     */
    static validateAddress(control: FormControl) {
        return / [0-9]/.test(control.value) === false? {missingHouseNumber: true} : null;
    }

    /**
     * Validating a postal code
     * @param control
     * @returns {Object|null}
     */
    static validateGermanPostalCode(control: FormControl) {
        /**
         * Regular expression that also exceps unicodes in emails
         * @type {RegExp}
         */
        let postalCodeExpression = /^[0-9]{5,5}$/;

        // test the value
        if (postalCodeExpression.test(control.value) === false) {
            return { postalCodeSucks: true };
        }
        return null;
    }

    /**
     * Validating an IBAN
     * @param control
     * @returns {Object|null}
     */
    static validateIBAN(control: FormControl) {
        // test the value
        if (IBAN.isValid(control.value) === false) {
            return { ibanSucks: true };
        }
        return null;
    }

    /**
     * Validating an BIC
     * @param control
     * @returns {Object|null}
     */
    static validateBIC(control: FormControl) {
        /**
         * Regular expression for validating the BIC
         * @type {RegExp}
         */
        let bicExpression = /([a-zA-Z]{4}[a-zA-Z]{2}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?)/;

        // test the value
        if (bicExpression.test(control.value) === false) {
            return { bicSucks: true };
        }
        return null;
    }

    /**
     * Validate spanish CIF (tax id for companies) string
     * @param control
     * @returns {Object|null}
     */
    static validateCIF(control: FormControl) {
        /**
         * Regular expression that validates a spanish CIF format
         * @type {RegExp}
         */
        let cifExpression = /^[a-zA-Z][0-9]{7}[a-zA-Z0-9]$/;

        // test the value
        if (cifExpression.test(control.value) === false) {
            return { cifSucks: true };
        }

        return null;
    }

    /**
     * validate spanish NIF string
     * @param control
     * @returns {Object|null}
     */
    static validateNIF(control: FormControl) {
        /**
         * Regular expression that validates a spanish NIF format
         * @type {RegExp}
         */
        let nifExpression = /^[a-zA-Z]?[0-9]{7,8}-?[a-zA-Z]$/;

        // test the value
        if (nifExpression.test(control.value) === false) {
            return { nifSucks: true };
        }

        return null;
    }

    /**
     * validate birthday from a german date string dd.mm.yyyy
     * - if it is a valid date
     * - if it is in the past
     * - if it is not older than 120 years
     * @param control
     * @returns {Object|null}
     */
    static validateBirthday(control: FormControl) {
        if (!control.value) return null;

        const [day, month, year] = control.value.split('.').map(Number);

        // Basic validation for numbers and ranges
        if (!day || !month || !year || month < 1 || month > 12 || day < 1 || day > 31) {
            return { birthdaySucks: true };
        }

        const givenDate = new Date(year, month - 1, day);
        const nowDate = new Date();
        const minDate = new Date();
        minDate.setFullYear(nowDate.getFullYear() - 120);

        // Check if the parsed date matches the input (to handle invalid dates like 31.02.2020)
        if (
            givenDate.getFullYear() !== year ||
            givenDate.getMonth() + 1 !== month ||
            givenDate.getDate() !== day ||
            givenDate > nowDate ||
            givenDate < minDate
        ) {
            return { birthdaySucks: true };
        }

        return null;
    }

}
